import { useEffect, useRef, type FormEvent } from "react";
import { CornerDownLeft, Edit3, Loader2, Send, Square, X } from "lucide-react";

/** Auto-grow bounds for the textarea, in px. */
const COMPOSER_MIN_H = 40;
const COMPOSER_MAX_H = 180;

export interface TestingComposerProps {
	value: string;
	onChange: (next: string) => void;
	onSubmit: (e: FormEvent) => void;
	/** False while this session is executing or the user is signed out — input is refused. */
	canSend: boolean;
	/** The planner is streaming; Send becomes Cancel. */
	streaming: boolean;
	onCancelStreaming: () => void;
	/** Why input is unavailable, shown in place of the hint row. */
	disabledReason?: string | null;

	/** Edit mode: the next message updates this plan instead of creating a new one. */
	editingPlanName?: string | null;
	onCancelEdit?: () => void;
}

/**
 * The message composer.
 *
 * Was a bare textarea with a detached square button — visually unrelated to the conversation above
 * it and with no affordance beyond the placeholder. This is one rounded shell that owns the input
 * and its controls, matching Weave's composer and the products the module is benchmarked against.
 *
 * Concrete fixes: the textarea auto-grows to a bound instead of staying one row until it scrolls;
 * the send control states its keyboard shortcut; the edit-mode chip is inside the shell so it reads
 * as a property of the message being composed rather than a floating banner; and the whole thing
 * sits on the same reading measure as the thread, so the input's edges line up with the messages.
 */
export function TestingComposer({
	value,
	onChange,
	onSubmit,
	canSend,
	streaming,
	onCancelStreaming,
	disabledReason,
	editingPlanName,
	onCancelEdit,
}: TestingComposerProps) {
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	// Grow with the content up to a bound, then scroll — so a multi-line scenario is fully visible
	// while being typed without the input ever eating the conversation.
	useEffect(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = "auto";
		el.style.height = `${Math.min(Math.max(el.scrollHeight, COMPOSER_MIN_H), COMPOSER_MAX_H)}px`;
	}, [value]);

	const submittable = canSend && value.trim().length > 0;

	return (
		<div className="tst-composer-dock shrink-0">
			<div className="tst-measure py-2.5">
				<form
					className="tst-composer-shell flex flex-col"
					onSubmit={(e) => {
						e.preventDefault();
						if (submittable) onSubmit(e);
					}}
				>
					{editingPlanName && (
						<div className="flex items-center gap-1.5 border-b border-primary/25 bg-primary/10 px-3 py-1.5 text-[11px] text-primary">
							<Edit3 className="h-3 w-3 shrink-0" />
							<span className="min-w-0 flex-1 truncate">
								Editing <span className="font-semibold">{editingPlanName}</span> — your message
								updates it
							</span>
							{onCancelEdit && (
								<button
									type="button"
									onClick={onCancelEdit}
									className="tst-interactive shrink-0 rounded p-0.5 text-primary/80 hover:bg-primary/15 hover:text-primary"
									title="Stop editing — your next message creates a new test instead"
									aria-label="Stop editing this plan"
								>
									<X className="h-3 w-3" />
								</button>
							)}
						</div>
					)}

					<textarea
						ref={textareaRef}
						value={value}
						onChange={(e) => onChange(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && !e.shiftKey) {
								e.preventDefault();
								if (e.repeat) return;
								if (submittable && !streaming) onSubmit(e);
							}
						}}
						rows={1}
						disabled={!canSend}
						aria-label={editingPlanName ? `Edit ${editingPlanName}` : "Describe a test"}
						placeholder={
							disabledReason ??
							(editingPlanName
								? `Change ${editingPlanName} — e.g. "also click Save"`
								: "Describe a test — e.g. \"Convert a Lead and check the Opportunity\"")
						}
						className="w-full resize-none bg-transparent px-3 pb-1 pt-2.5 text-[13px] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/60 disabled:cursor-not-allowed disabled:opacity-60"
						style={{ minHeight: COMPOSER_MIN_H, maxHeight: COMPOSER_MAX_H, overflowY: "auto" }}
					/>

					<div className="flex items-center gap-2 px-2.5 pb-2 pt-0.5">
						{/* The reason already fills the placeholder of the disabled textarea; repeating it
						    here printed the same sentence twice, one line apart. */}
						<span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground/70">
							{!disabledReason && (
								<span className="hidden items-center gap-1 group-data-[cw-md]/chat:inline-flex">
									<CornerDownLeft className="h-3 w-3" />
									to send · Shift+Enter for a new line
								</span>
							)}
						</span>
						{streaming ? (
							<button
								type="button"
								onClick={onCancelStreaming}
								className="tst-interactive inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-destructive/40 px-3 text-[12px] font-medium text-destructive-strong hover:bg-destructive/10"
								title="Stop the planner"
							>
								<Square className="h-3.5 w-3.5" />
								Stop
							</button>
						) : (
							<button
								type="submit"
								disabled={!submittable}
								title="Send (Enter)"
								aria-label="Send"
								className="tst-interactive inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
							>
								{canSend ? <Send className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
							</button>
						)}
					</div>
				</form>
			</div>
		</div>
	);
}
